import { db,storage  } from '../Firebase/firebase';
import {
  collection,
  where,
  addDoc,
  query,
  orderBy,
  onSnapshot, // For real-time updates
  limit,
  doc,
  setDoc,
  getDoc,
  updateDoc, // Import updateDoc
  serverTimestamp // Use server timestamp for consistency
} from 'firebase/firestore';
import { writeBatch } from 'firebase/firestore'; // Import writeBatch for efficiency
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { v4 as uuidv4 } from 'uuid'; // For generating unique file names
// import { encryptMessage } from './cryptoService'; // Adjust path
// import { getMyStoredSecretKey, getUserPublicKey } from './userService'; // Assuming these helpers



// Function to generate the unique chat ID (mirrors the one in context)
const generateChatId = (userId1, userId2) => {
  if (!userId1 || !userId2) throw new Error("Both user IDs must be provided to generate a chat ID");
  return [userId1, userId2].sort().join('_');
};

// --- Modified sendMessage ---
// Now accepts an optional 'file' object and 'type'
export const sendMessage = async (chatId, senderId, receiverId, messageData) => {
    if (!chatId || !senderId || !receiverId || !messageData) {
        console.error("Missing required fields for sending message.");
        return;
    }

    const { type = 'text', text = '', fileUrl = '', fileName = '', fileType = '' } = messageData;

    // Basic validation
    if (type === 'text' && !text.trim()) {
        console.warn("Cannot send empty text message.");
        return;
    }
    if ((type === 'image' || type === 'file') && !fileUrl) {
         console.error("File URL missing for file message.");
        return;
    }

    try {
        const chatRef = doc(db, 'chats', chatId);
        const messagesColRef = collection(chatRef, 'messages');

        const messagePayload = {
            senderId,
            receiverId,
            type,
            timestamp: serverTimestamp(),
            read: false,
            // encryptionType: "e2ee/nacl.box.v1" // Example versioning
        };

        if (type === 'text') {
            if (!text.trim()) { /* ... */ return; }

            // --- E2EE Encryption Step ---
            // const mySecretKey = await getMyStoredSecretKey(senderId); // Implement this to retrieve from local storage
            // const theirPublicKey = await getUserPublicKey(receiverId); // Implement this to fetch from Firestore

            // if (!mySecretKey || !theirPublicKey) {
            //     console.error("Encryption keys are missing. Cannot send message.");
            //     throw new Error("Keys unavailable for E2EE.");
            // }

            // const { ciphertext, nonce } = encryptMessage(text.trim(), theirPublicKey, mySecretKey);
            // messagePayload.ciphertext = ciphertext; // Store encrypted content
            // messagePayload.nonce = nonce;           // Store nonce needed for decryption
            // --- End E2EE Step ---

            // Store plain text for now
            messagePayload.text = text.trim();
        } else { // image or file
            messagePayload.fileUrl = fileUrl;
            messagePayload.fileName = fileName;
            messagePayload.fileType = fileType;
        }

        // 1. Add the message to the subcollection
        const messageDocRef = await addDoc(messagesColRef, messagePayload);
        console.log("Message added with ID:", messageDocRef.id);

        // Update last message - this will now be generic
        let lastMessageTextDisplay = type === 'text' ? text.trim() : (type === 'image' ? '📷 Image' : `📄 File: ${fileName || 'File'}`);

        await setDoc(chatRef, {
            participants: [senderId, receiverId],
            lastMessage: {
                text: lastMessageTextDisplay,
                timestamp: serverTimestamp(),
                senderId: senderId,
                isEncrypted: false
            },
            updatedAt: serverTimestamp()
        }, { merge: true });

        console.log("Message sent and chat metadata updated successfully!");

    } catch (error) {
        console.error("Error sending message:", error);
        throw error;
    }
};

// --- NEW: File Upload Service ---
export const uploadChatFile = (chatId, userId, file, onProgress) => {
    return new Promise(async (resolve, reject) => {
        if (!file || !chatId || !userId) {
            return reject("Missing file, chatId, or userId for upload.");
        }

        // Create a unique path/filename for the file in Storage
        const fileExtension = file.name.split('.').pop();
        const uniqueFileName = `${uuidv4()}.${fileExtension}`;
        const storagePath = `chatFiles/${chatId}/${userId}/${uniqueFileName}`;
        const storageRef = ref(storage, storagePath);

        console.log(`Uploading file to: ${storagePath}`);

        const uploadTask = uploadBytesResumable(storageRef, file);

        uploadTask.on('state_changed',
            (snapshot) => {
                // Progress function (optional)
                const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                console.log('Upload is ' + progress + '% done');
                if (onProgress) {
                    onProgress(progress); // Call the callback with progress percentage
                }
                // Handle paused/running states if needed
                // switch (snapshot.state) {
                //   case 'paused': console.log('Upload is paused'); break;
                //   case 'running': console.log('Upload is running'); break;
                // }
            },
            (error) => {
                // Handle unsuccessful uploads
                console.error("Upload failed:", error);
                // A full list of error codes is available at
                // https://firebase.google.com/docs/storage/web/handle-errors
                switch (error.code) {
                    case 'storage/unauthorized':
                        reject('User does not have permission to access the object');
                        break;
                    case 'storage/canceled':
                         reject('User canceled the upload');
                        break;
                    case 'storage/unknown':
                         reject('Unknown error occurred, inspect error.serverResponse');
                        break;
                    default:
                        reject(`Upload failed: ${error.message}`);
                }
            },
            async () => {
                // Handle successful uploads on complete
                console.log('Upload successful!');
                try {
                    const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                    console.log('File available at', downloadURL);
                    resolve({
                        url: downloadURL,
                        path: storagePath, // Optional: return path if needed
                        name: file.name,   // Return original name
                        type: file.type    // Return MIME type
                    });
                } catch (getUrlError) {
                     console.error("Error getting download URL:", getUrlError);
                     reject("Upload succeeded, but failed to get download URL.");
                }
            }
        );
    });
};

// --- Typing Status ---
// Function to update the typing status for a user in a chat
export const updateUserTypingStatus = async (chatId, userId, isTyping) => {
  if (!chatId || !userId) return;
  try {
      const chatRef = doc(db, 'chats', chatId);
      // Use updateDoc to specifically update the typingStatus field
      // for the current user without overwriting the other user's status.
      // Use dot notation for nested fields.
      await updateDoc(chatRef, {
          [`typingStatus.${userId}`]: isTyping
      });
  } catch (error) {
      // Check if the error is because the document doesn't exist (e.g., first message not sent)
      if (error.code === 'not-found') {
           // Optionally create the chat doc here if it should exist even before messages
           console.warn(`Chat doc ${chatId} not found for typing status update. It might be created upon the first message.`);
           // Or you could call ensureChatExists here if preferred.
      } else {
           console.error("Error updating typing status:", error);
      }
  }
};

// --- Message Listener (Modified) ---
// Modify the listener to listen to the main chat document *as well* for typing status
// We will create a *new* listener specifically for the chat document itself.

// Listener for the specific Chat Document (including typing status)
export const getChatDocumentListener = (chatId, callback) => {
  if (!chatId) {
      console.warn("Chat ID is missing, cannot fetch chat document.");
      return () => {};
  }
  try {
      const chatRef = doc(db, 'chats', chatId);
      const unsubscribe = onSnapshot(chatRef, (docSnap) => {
          if (docSnap.exists()) {
              callback(docSnap.data()); // Pass the whole chat document data
          } else {
              // Handle case where chat doc might not exist yet
              callback(null);
              console.warn(`Chat document ${chatId} does not exist yet.`);
          }
      }, (error) => {
          console.error("Error fetching real-time chat document:", error);
          callback(null); // Send null on error
      });
      return unsubscribe;
  } catch (error) {
      console.error("Error setting up chat document listener:", error);
      return () => {};
  }
};

// Function to get a real-time listener for messages in a specific chat
export const getChatMessagesListener = (chatId, callback) => {
  if (!chatId) {
      console.warn("Chat ID is missing, cannot fetch messages.");
      // Return an empty unsubscribe function or handle appropriately
      return () => {};
  }
  try {
      const messagesColRef = collection(db, 'chats', chatId, 'messages');
      const q = query(messagesColRef, orderBy('timestamp', 'asc'), limit(100)); // Order by time, limit messages fetched initially

      // onSnapshot returns an unsubscribe function
      const unsubscribe = onSnapshot(q, (querySnapshot) => {
          const messages = querySnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
          }));
          callback(messages); // Pass messages to the callback function provided by the component
      }, (error) => {
          console.error("Error fetching real-time messages:", error);
          // Handle error appropriately (e.g., update UI state)
          callback([]); // Send empty array on error or handle differently
      });

      return unsubscribe; // Return the unsubscribe function to be called on cleanup

  } catch (error) {
      console.error("Error setting up message listener:", error);
       return () => {}; // Return dummy unsubscribe on initial setup error
  }
};


// Function to get a listener for user's chats/conversations (optional, for chat list)
export const getUserChatsListener = (userId, callback) => {
    if (!userId) return () => {};

    try {
        const chatsRef = collection(db, 'chats');
        // Query chats where the participants array contains the current user's ID
        const q = query(
            chatsRef,
            where('participants', 'array-contains', userId),
            orderBy('updatedAt', 'desc') // Order by most recently updated
        );

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const chats = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            callback(chats);
        }, (error) => {
            console.error("Error fetching user chats:", error);
            callback([]);
        });

        return unsubscribe;

    } catch (error) {
        console.error("Error setting up user chats listener:", error);
        return () => {};
    }
}

// Helper to ensure a chat document exists (can be called before opening a chat)
export const ensureChatExists = async (userId1, userId2) => {
    const chatId = generateChatId(userId1, userId2);
    const chatRef = doc(db, 'chats', chatId);
    const chatSnap = await getDoc(chatRef);

    if (!chatSnap.exists()) {
        try {
            await setDoc(chatRef, {
                participants: [userId1, userId2],
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(), // Initialize updatedAt
                lastMessage: null // Initialize lastMessage
            });
            console.log(`Chat document created for ${chatId}`);
        } catch (error) {
            console.error(`Failed to create chat document for ${chatId}:`, error);
        }
    }
    return chatId; // Return the ID whether it existed or was created
}

// Function to mark messages as read *up to a certain point* or *specific messages*
// Option 1: Mark specific message(s) - simpler for basic receipts
export const markMessagesAsRead = async (chatId, messageIds) => {
    if (!chatId || !messageIds || messageIds.length === 0) return;

    const chatRef = doc(db, 'chats', chatId);
    const messagesColRef = collection(chatRef, 'messages');

    // Use a batch write for efficiency if marking multiple messages
    const batch = writeBatch(db);

    messageIds.forEach(messageId => {
        const messageRef = doc(messagesColRef, messageId);
        batch.update(messageRef, { read: true });
    });

    try {
        await batch.commit();
        // console.log(`Marked ${messageIds.length} messages as read in chat ${chatId}`);
    } catch (error) {
        console.error("Error marking messages as read:", error);
    }
};

// Option 2: (More Advanced) Update lastReadTimestamp for the user in the chat doc
// export const updateLastReadTimestamp = async (chatId, userId) => {
//    if (!chatId || !userId) return;
//    const chatRef = doc(db, 'chats', chatId);
//    try {
//       await updateDoc(chatRef, {
//          [`readStatus.${userId}.lastRead`]: serverTimestamp()
//       });
//    } catch (error) {
//       console.error("Error updating last read timestamp:", error);
//    }
// }